import {
  billingPeriodFromStripePrice,
  convertBillingPeriodToNiceName,
  getPriceForBillingPeriod,
  getTotalMonthsInBillingPeriod,
  getUnitAmountFromPrice,
} from "@/lib/utils/subscription";
import { CustomStripePrice } from "@/types/product-schema";
import { SegmentedControl, Text } from "@mantine/core";
import { useMediaQuery } from "@mui/material";
import { useMemo } from "react";
import classes from "./BillingPeriodSelector.module.css";

export default function BillingPeriodSelector({
  selectedPeriod,
  onPeriodChange,
  referencePrices,
  disabled = false,
}: {
  selectedPeriod: string;
  onPeriodChange: (period: string) => void;
  referencePrices: CustomStripePrice[];
  disabled?: boolean;
}) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  // Derive available billing intervals
  const availableIntervals = useMemo(() => {
    const billingPeriodSet = new Set<string>();

    referencePrices.forEach((price) => {
      billingPeriodSet.add(billingPeriodFromStripePrice(price));
    });

    const sortedBillingPeriods = Array.from(billingPeriodSet).sort((a, b) => {
      const [intervalA, countA] = a.split("-");
      const [intervalB, countB] = b.split("-");

      if (intervalA === "month" && intervalB === "year") return -1;
      if (intervalA === "year" && intervalB === "month") return 1;

      const numA = parseInt(countA || "0");
      const numB = parseInt(countB || "0");

      return numA - numB;
    });

    const monthlyUSDPriceDecimal = getUnitAmountFromPrice(
      getPriceForBillingPeriod(referencePrices, "usd", "month-1")
    );

    return sortedBillingPeriods.map((billingPeriod, index) => {
      let discountBadge = null;
      const discount = Math.round(
        calculateDiscountOverMonthlyPrice(
          monthlyUSDPriceDecimal,
          getPriceForBillingPeriod(referencePrices, "usd", billingPeriod),
          billingPeriod
        )
      );

      return {
        label: (
          <Text className={classes.periodSelectorLabel}>
            {`${convertBillingPeriodToNiceNameWithMobile(
              billingPeriod,
              isMobile
            )}${discountBadge ? ` (${discountBadge})` : ""}`}
            {discount > 0 && (
              <span className={classes.discountBadge}>-{discount}%</span>
            )}
          </Text>
        ),
        value: billingPeriod,
      };
    });
  }, [referencePrices, isMobile]);

  return (
    <SegmentedControl
      size="sm"
      data={availableIntervals}
      value={selectedPeriod}
      onChange={onPeriodChange}
      className={classes.segmentedControl}
      fullWidth={isMobile}
      disabled={disabled}
    />
  );
}

export const useMembershipFilterStyles = () => ({ classes });

function convertBillingPeriodToNiceNameWithMobile(
  billingPeriod: string,
  isMobile: boolean
) {
  const niceName = convertBillingPeriodToNiceName(billingPeriod);
  if (isMobile) {
    return (
      "/" +
      niceName
        .replace("-Monthly", "m")
        .replace("-Yearly", "y")
        .replace("Monthly", "month")
        .replace("Yearly", "year")
    );
  }
  return niceName;
}

function calculateDiscountOverMonthlyPrice(
  monthlyUSDPriceDecimal: number,
  comparisonPrice: CustomStripePrice,
  comparisonBillingPeriod: string
) {
  const comparisonPriceAmount = getUnitAmountFromPrice(comparisonPrice);
  return (
    (1 -
      comparisonPriceAmount /
        (monthlyUSDPriceDecimal *
          getTotalMonthsInBillingPeriod(comparisonBillingPeriod))) *
    100
  );
}
